import { withErrorBoundary } from 'app/components/error-boundary';
import { Page } from 'app/design/primitives/page';
import type { NavigationScreen, SpecificRouteProp } from 'app/types/navigation';
import { Column } from 'app/design/primitives/box';
import FancySeparator from 'app/design/images/fancy-separator';
import { Button } from 'app/design/primitives/button';
import { Accessory } from 'app/design/primitives/page/accessory';
import { useAllAddresses } from 'app/network/address';
import { useQueryKeys } from 'app/network/invalidation';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import Animated, {
  cancelAnimation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import Balloon1 from 'app/design/images/balloon1';
import Balloon2 from 'app/design/images/balloon2';
import { useDeviceHeight, useDeviceWidth } from 'app/hooks/use-device-size';
import { Event, track } from 'app/lib/analytics';
import { useUserCurrency } from 'app/network/user';
import { ListingType } from 'app/types/listing';
import { useOpenOffer } from 'app/hooks/use-open-offer';
import { FreeSubActivatedSubscription } from './free-sub-activated-subscription';
import { FreeSubClosing } from './free-sub-closing';
import { FreeSubContributorCelebration } from './free-sub-contributor-celebration';
import { FreeSubHeader } from './free-sub-header';
import { FreeSubPendingOfferProgress } from './free-sub-pending-offer-progress';

export type FreeSubParams = SpecificRouteProp<'freeSub'>['params'];

interface FreeSubActionsProps {
  goToInvite: () => void;
  goToNewListing: () => void;
  goToSecondary: () => void;
  userGotFreeSub: FreeSubParams['userGotFreeSub'];
  userNowContributor: boolean;
}

function FreeSubActions({
  goToInvite,
  goToNewListing,
  goToSecondary,
  userGotFreeSub,
  userNowContributor,
}: FreeSubActionsProps) {
  const { t } = useTranslation('free-sub');

  return (
    <Accessory marginHorizontal="spacing.general.x4">
      <Column gap="spacing.general.x4" width="100%">
        <Button onPress={() => (userGotFreeSub ? goToInvite() : goToNewListing())} variant="primary">
          {userGotFreeSub ? t('inviteButtonText') : t('createOfferButtonText')}
        </Button>

        <Button onPress={goToSecondary} variant="secondary">
          {userNowContributor ? t('myImpactLevelButtonText') : t('billboardButtonText')}
        </Button>
      </Column>
    </Accessory>
  );
}

function getOffersCreated(
  userGotFreeSub: FreeSubParams['userGotFreeSub'],
  numOffersCreated: number | false | undefined,
) {
  const offersCreated: string[] = [];
  if (!userGotFreeSub && numOffersCreated) {
    for (let i = 1; i <= numOffersCreated; i++) {
      offersCreated.push(i.toString());
    }
  }
  return offersCreated;
}

const FreeSubScreenComponent: NavigationScreen<'freeSub'> = ({ navigation, route }) => {
  const qk = useQueryKeys();

  const w = useDeviceWidth();

  const { data: addresses } = useAllAddresses();
  const firstAddress = addresses?.[0];

  const userCurrency = useUserCurrency();

  const userGotFreeSub = route.params.userGotFreeSub;

  // Force a re-fetch of the user's address, now that they received their free subscription
  useEffect(() => {
    if (!userGotFreeSub) return;
    void qk.myAddresses.invalidate();
  }, [qk, userGotFreeSub]);
  const numOffersCreated = route.params.numOffersCreated;
  const userNowContributor = route.params.userNowContributor;
  const finishedTutorial = route.params.finishedTutorial;

  const offersCreated = getOffersCreated(userGotFreeSub, numOffersCreated);

  useEffect(() => {
    if (firstAddress && userGotFreeSub) {
      track(userGotFreeSub === 'defricheur' ? Event.OffreDefricheurActivated : Event.OffreTrialActivated, {
        address: firstAddress,
      });
    }
  }, [firstAddress, userGotFreeSub]);

  const goToSecondary = () => {
    navigation.navigate('billboard', { screen: 'all-listings' });
  };

  const goToInvite = () => {
    navigation.navigate('billboard', { screen: 'all-listings' });
    // Use setTimout to run this only once the screen dismiss animation (from above navigation) completes
    setTimeout(() => navigation.navigate('invite', undefined, { pop: true }), 10);
  };

  const openOffer = useOpenOffer();
  const goToNewListing = () => {
    navigation.navigate('billboard', { screen: 'all-listings' });
    // Use setTimout to run this only once the screen dismiss animation (from above navigation) completes
    setTimeout(
      () =>
        openOffer(navigation, {
          defaultValues: { type: ListingType.offer },
          newListingSource: 'free_subscription',
        }),
      10,
    );
  };

  // Animated balloons

  const dh = useDeviceHeight();

  const bal1Top = useSharedValue(dh); // start at bottom of the screen
  const bal1Left = useSharedValue(0);
  const bal1Spin = useSharedValue(0);
  const bal2Top = useSharedValue(dh); // start at bottom of the screen
  const bal2Right = useSharedValue(0);
  const bal2Spin = useSharedValue(0);

  useEffect(() => {
    bal1Top.set(withTiming(-150, { duration: 5000 }));
    bal1Left.set(
      withSequence(
        withTiming(80, { duration: 1500 }),
        withTiming(-15, { duration: 1500 }),
        withTiming(10, { duration: 2000 }),
      ),
    );
    bal1Spin.set(
      withSequence(
        withTiming(1, { duration: 2000 }),
        withTiming(0, { duration: 1500 }),
        withTiming(1, { duration: 1500 }),
      ),
    );
    bal2Top.set(withTiming(-150, { duration: 6000 }));
    bal2Right.set(
      withSequence(
        withTiming(80, { duration: 2000 }),
        withTiming(-15, { duration: 2000 }),
        withTiming(35, { duration: 2000 }),
      ),
    );
    bal2Spin.set(
      withSequence(
        withTiming(1, { duration: 1500 }),
        withTiming(0, { duration: 3000 }),
        withTiming(1, { duration: 1500 }),
      ),
    );

    return () => {
      [bal1Top, bal1Left, bal1Spin, bal2Top, bal2Right, bal2Spin].forEach(cancelAnimation);
    };
  }, [bal1Top, bal1Left, bal1Spin, bal2Top, bal2Right, bal2Spin]);

  const balloon1Style = useAnimatedStyle(() => ({
    left: bal1Left.get(),
    top: bal1Top.get(),
    transform: [{ rotate: `${String(interpolate(bal1Spin.get(), [0, 1], [-20, 45]))}deg` }],
  }));
  const balloon2Style = useAnimatedStyle(() => ({
    right: bal2Right.get(),
    top: bal2Top.get(),
    transform: [{ rotate: `${String(interpolate(bal2Spin.get(), [0, 1], [-45, 20]))}deg` }],
  }));

  return (
    <Page
      accessory={
        <FreeSubActions
          goToInvite={goToInvite}
          goToNewListing={goToNewListing}
          goToSecondary={goToSecondary}
          userGotFreeSub={userGotFreeSub}
          userNowContributor={userNowContributor}
        />
      }
      stretchToFill
    >
      <Animated.View style={[{ position: 'absolute' }, balloon1Style]}>
        <Balloon1 />
      </Animated.View>
      <Animated.View style={[{ position: 'absolute' }, balloon2Style]}>
        <Balloon2 />
      </Animated.View>

      <Column
        alignItems="center"
        gap="spacing.general.x8"
        marginTop="spacing.general.x8"
        paddingHorizontal="spacing.general.x8"
        paddingTop="spacing.general.x12"
      >
        <FreeSubHeader finishedTutorial={finishedTutorial} userGotFreeSub={userGotFreeSub} />

        <FancySeparator width={w - 2 * 40} />

        <FreeSubActivatedSubscription userCurrency={userCurrency} userGotFreeSub={userGotFreeSub} width={w} />
        <FreeSubPendingOfferProgress
          numOffersCreated={numOffersCreated}
          offersCreated={offersCreated}
          userGotFreeSub={userGotFreeSub}
        />

        <FancySeparator width={w - 2 * 40} />

        <FreeSubContributorCelebration userNowContributor={userNowContributor} width={w} />
        <FreeSubClosing userGotFreeSub={userGotFreeSub} userNowContributor={userNowContributor} />
      </Column>
    </Page>
  );
};

export const FreeSubScreen: NavigationScreen<'freeSub'> = withErrorBoundary(FreeSubScreenComponent);
